import React, {useEffect, useState} from "react";
import axios from "axios";
import ReportTable from "../common/ReportTable";
import TableSkeleton from "../../utils/TableSkeleton";
import EmptyTableState from "../../utils/EmptyTableState";
import * as Constants from "../../utils/Constants";
import {customStyles} from "../../utils/DataTableCustomStyle";
import ReportAssetFilter from "../ReportAssetFilter";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFileExcel, faFilePdf} from "@fortawesome/free-solid-svg-icons";
import * as XLSX from "xlsx";
import FileSaver from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {getAuthToken, getClientIdOfUser, showToast} from "../../utils/utility";
import "../../styles/components/reports.css";

const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

const Gt06DailyActivity = () => {
  const [assetList, setAssetList] = useState([]);
  const [checkedItems, setCheckedItems] = useState({});
  const TODAY = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(TODAY);
  const [endDate, setEndDate] = useState(TODAY);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
        const res = await axios.get(
          `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/${Constants.EP_ASSETS_GET_ALL}`
        );
        const all = res.data?.responseData || [];
        setAssetList(all.filter((a) => (a.assetType || "").toUpperCase() === "GT06_TRACKER"));
      } catch (err) {
        console.error("Error fetching GT06 assets:", err);
      }
    })();
  }, []);

  const onSearch = async () => {
    const ids = Object.keys(checkedItems).filter((k) => checkedItems[k]);
    if (!startDate || !endDate) {
      showToast("Please select a date range.", {type: "error"});
      return;
    }
    setIsLoading(true);
    try {
      const params = new URLSearchParams({startDate, endDate, timezone: TZ});
      if (ids.length) params.set("assetIds", ids.join(","));
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const res = await axios.get(
        `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/gt06-reports/daily-activity?${params}`
      );
      const enriched = (res.data || []).map((r) => ({
        ...r,
        assetName: assetList.find((a) => a.assetUnitId === r.assetUnitId)?.name || r.assetUnitId,
      }));
      setRows(enriched);
    } catch (err) {
      console.error("Daily activity failed:", err);
      showToast(err?.response?.data?.responseMessage || "Failed to load daily activity", {type: "error"});
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = (id) =>
    setCheckedItems((p) => ({...p, [id]: !p[id]}));

  const handleClear = () => {
    const today = new Date().toISOString().split("T")[0];
    setStartDate(today);
    setEndDate(today);
    setCheckedItems({});
    setRows([]);
  };

  const min = (m) => (m == null ? "—" : `${Math.round(m)}m`);

  const columns = [
    {name: "Date", id: "Date", selector: (r) => r.date, sortable: true, grow: 0.9, minWidth: "100px"},
    {name: "Asset", id: "Asset", selector: (r) => r.assetName, sortable: true, grow: 2, minWidth: "130px"},
    {name: "Distance (km)", id: "Distance (km)", selector: (r) => r.distanceKm, sortable: true, grow: 1.2, minWidth: "110px"},
    {name: "Trips", id: "Trips", selector: (r) => r.tripCount, sortable: true, grow: 0.6, minWidth: "70px"},
    {name: "Engine Hrs", id: "Engine Hrs", selector: (r) => r.engineHoursOn, sortable: true, grow: 1.1, minWidth: "110px"},
  ];

  const ExpandedRow = ({data: r}) => {
    const fields = [
      ["IMEI", r.assetUnitId],
      ["Moving", r.movingMinutes != null ? `${Math.round(r.movingMinutes)} min` : "—"],
      ["Idle", r.idleMinutes != null ? `${Math.round(r.idleMinutes)} min` : "—"],
      ["Stop", r.stopMinutes != null ? `${Math.round(r.stopMinutes)} min` : "—"],
      ["Max Speed", r.maxSpeedKmph != null ? `${r.maxSpeedKmph} km/h` : "—"],
      ...(r.alerts && Object.keys(r.alerts).length > 0
        ? Object.entries(r.alerts).map(([k, v]) => [k.replace(/_/g, " "), v])
        : []),
    ];
    return (
      <div style={{display: "flex", flexWrap: "wrap", padding: "10px 20px 12px", background: "#fafbff", borderBottom: "2px solid #e9ecef", borderLeft: "4px solid #FF683A"}}>
        {fields.map(([label, value], i, arr) => (
          <div key={label} style={{display: "flex", flexDirection: "column", gap: "3px", padding: "2px 20px 2px 0", marginRight: "20px", borderRight: i < arr.length - 1 ? "1px solid #e5e7eb" : "none"}}>
            <span style={{fontSize: "0.62rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em"}}>{label}</span>
            <span style={{fontSize: "0.84rem", fontWeight: 500, color: "#1f2937"}}>{value}</span>
          </div>
        ))}
      </div>
    );
  };

  const exportExcel = () => {
    if (!rows.length) return;
    const ws = XLSX.utils.json_to_sheet(
      rows.map((r) => ({
        Date: r.date,
        Asset: r.assetName,
        IMEI: r.assetUnitId,
        DistanceKm: r.distanceKm,
        EngineHoursOn: r.engineHoursOn,
        MovingMin: r.movingMinutes,
        IdleMin: r.idleMinutes,
        StopMin: r.stopMinutes,
        Trips: r.tripCount,
        MaxKmph: r.maxSpeedKmph,
        Alerts: r.alerts ? JSON.stringify(r.alerts) : "",
      }))
    );
    const wb = {Sheets: {data: ws}, SheetNames: ["DailyActivity"]};
    const buf = XLSX.write(wb, {bookType: "xlsx", type: "array"});
    FileSaver.saveAs(new Blob([buf]), `gt06_daily_activity_${Date.now()}.xlsx`);
  };

  const exportPdf = () => {
    if (!rows.length) return;
    const doc = new jsPDF("l", "pt", "a4");
    doc.text(`GT06 Daily Activity — ${startDate} to ${endDate}`, 30, 30);
    autoTable(doc, {
      startY: 50,
      head: [["Date", "Asset", "IMEI", "Km", "EngHrs", "Move(m)", "Idle(m)", "Stop(m)", "Trips", "Max km/h"]],
      body: rows.map((r) => [
        r.date, r.assetName, r.assetUnitId, r.distanceKm, r.engineHoursOn,
        Math.round(r.movingMinutes), Math.round(r.idleMinutes), Math.round(r.stopMinutes),
        r.tripCount, r.maxSpeedKmph,
      ]),
      styles: {fontSize: 8},
    });
    doc.save(`gt06_daily_activity_${Date.now()}.pdf`);
  };

  return (
    <div className="report-content-split">
      <div className="report-main-area">
        <div className="report-header-actions">
          <span className="report-results-title">
            Daily Vehicle Activity {rows.length > 0 && `(${rows.length})`}
          </span>
          {rows.length > 0 && (
            <div className="export-buttons">
              <button className="export-btn" onClick={exportExcel}>
                <FontAwesomeIcon icon={faFileExcel} /> Excel
              </button>
              <button className="export-btn" onClick={exportPdf}>
                <FontAwesomeIcon icon={faFilePdf} /> PDF
              </button>
            </div>
          )}
        </div>
        <div className="report-table-wrapper">
          <ReportTable
            columns={columns}
            data={rows}
            customStyles={customStyles}
            progressPending={isLoading}
            progressComponent={<TableSkeleton />}
            fixedHeader
            fixedHeaderScrollHeight="100%"
            noDataComponent={<EmptyTableState variant="search" />}
            pagination
            paginationPerPage={25}
            paginationRowsPerPageOptions={[25, 50, 100]}
            expandableRows
            expandableRowExpanded={() => true}
            expandableRowsComponent={ExpandedRow}
          />
        </div>
      </div>
      <div className="report-filter-sidebar">
        <div className="sidebar-header">
          <h3 className="sidebar-title">Search Filters</h3>
        </div>
        <div className="sidebar-content">
          <div className="filter-group">
            <label className="filter-label">Select GPS Trackers</label>
            <div className="asset-list-wrapper">
              <ReportAssetFilter assets={assetList} checkedItems={checkedItems} setCheckedItems={setCheckedItems} />
            </div>
          </div>
          <div>
            <label className="filter-label">Start Date</label>
            <input
              type="date"
              className="filter-input"
              value={startDate}
              max={new Date().toISOString().split("T")[0]}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label className="filter-label">End Date</label>
            <input
              type="date"
              className="filter-input"
              value={endDate}
              max={new Date().toISOString().split("T")[0]}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
        <div className="sidebar-footer">
          <div className="filter-actions">
            <button className="btn-report" onClick={onSearch}>Search</button>
            <button className="btn-report btn-secondary" onClick={handleClear}>Clear</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Gt06DailyActivity;
